import { useState, useCallback, useEffect } from 'react';

export type TicketLoggerMode = 'idle' | 'selecting' | 'form';

export interface SelectedElement {
  label: string;
  rect: DOMRect;
  element: Element;
}

export interface TicketLoggerState {
  mode: TicketLoggerMode;
  selected: SelectedElement | null;
  screenshotBase64: string | null;
  startSelecting: () => void;
  cancelSelecting: () => void;
  setSelected: (el: SelectedElement, screenshot: string) => void;
  closeForm: () => void;
}

function deriveLabel(el: Element): string {
  const testId = (el as HTMLElement).dataset.testid;
  if (testId) return testId;
  const ariaLabel = el.getAttribute('aria-label');
  if (ariaLabel) return ariaLabel;
  const role = el.getAttribute('role');
  const tag = el.tagName.toLowerCase();
  const className = typeof el.className === 'string'
    ? el.className.split(' ').filter(c => c && !c.startsWith('text-') && !c.startsWith('bg-') && !c.startsWith('p-') && !c.startsWith('m-') && !c.startsWith('w-') && !c.startsWith('h-') && !c.startsWith('flex') && !c.startsWith('border') && !c.startsWith('rounded')).slice(0, 2).join(' ')
    : '';
  return [role, tag, className].filter(Boolean).join(' · ') || tag;
}

export function useTicketLogger(): TicketLoggerState {
  const [mode, setMode] = useState<TicketLoggerMode>('idle');
  const [selected, setSelectedState] = useState<SelectedElement | null>(null);
  const [screenshotBase64, setScreenshotBase64] = useState<string | null>(null);

  const startSelecting = useCallback(() => setMode('selecting'), []);
  const cancelSelecting = useCallback(() => setMode('idle'), []);
  const closeForm = useCallback(() => {
    setMode('idle');
    setSelectedState(null);
    setScreenshotBase64(null);
  }, []);

  const setSelected = useCallback((el: SelectedElement, screenshot: string) => {
    setSelectedState(el);
    setScreenshotBase64(screenshot);
    setMode('form');
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'T') {
        e.preventDefault();
        setMode(prev => prev === 'idle' ? 'selecting' : 'idle');
      }
      if (e.key === 'Escape') {
        setMode(prev => prev === 'selecting' ? 'idle' : prev);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  return { mode, selected, screenshotBase64, startSelecting, cancelSelecting, setSelected, closeForm };
}

export { deriveLabel };
